import { ImageUploadConfig, SUPPORTED_WEB_IMAGES } from '../config/imageUploadConfig';
import { validateImageFile, ValidationResult } from './image-validation.util';

export interface ImageMetadata {
  width: number;
  height: number;
  aspectRatio: number;
  fileSizeBytes: number;
  mimeType: string;
  fileName: string;
  hasTransparency?: boolean;
  orientation?: number;
}

export interface TransformResult {
  file: File;
  metadata: ImageMetadata;
  validationResult: ValidationResult;
}

export interface EditorTransformState {
  zoom: number;
  rotation: 0 | 90 | 180 | 270;
  focalX: number; // 0-1, normalized
  focalY: number; // 0-1, normalized
  backgroundMode: 'transparent' | 'white' | 'preserve';
  fitMode: 'contain' | 'cover';
}

export class ImageEditorError extends Error {
  constructor(public code: string, message: string) {
    super(message);
    this.name = 'ImageEditorError';
  }
}

export const readImageMetadata = (file: File): Promise<ImageMetadata> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        aspectRatio: img.naturalWidth / img.naturalHeight,
        fileSizeBytes: file.size,
        mimeType: file.type,
        fileName: file.name,
        hasTransparency: file.type === 'image/png' || file.type === 'image/webp' || file.type === 'image/gif',
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new ImageEditorError('DECODE_FAILED', 'Failed to read image metadata'));
    };
    img.src = url;
  });
};

export const decodeImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new ImageEditorError('DECODE_FAILED', 'Failed to decode image'));
    };
    img.src = url;
  });
};

export const resolveCanvasBackground = (config: ImageUploadConfig, metadata: ImageMetadata): string => {
  if (config.backgroundMode === 'transparent') return 'transparent';
  if (config.backgroundMode === 'white') return '#FFFFFF';
  if (config.backgroundMode === 'preserve') return metadata.hasTransparency ? 'transparent' : '#FFFFFF';
  return 'transparent';
};

export const resolveStateBackground = (stateMode: string, metadata: ImageMetadata): string => {
  if (stateMode === 'transparent') return 'transparent';
  if (stateMode === 'white') return '#FFFFFF';
  if (stateMode === 'preserve') return metadata.hasTransparency ? 'transparent' : '#FFFFFF';
  return 'transparent';
};

interface ContainRect {
  canvasWidth: number;
  canvasHeight: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export const calculateContainRect = (srcW: number, srcH: number, tgtW: number, tgtH: number): ContainRect => {
  const srcRatio = srcW / srcH;
  const tgtRatio = tgtW / tgtH;
  
  let drawW = tgtW;
  let drawH = tgtH;
  
  if (srcRatio > tgtRatio) {
    drawH = tgtW / srcRatio;
  } else {
    drawW = tgtH * srcRatio;
  }
  
  return {
    canvasWidth: tgtW,
    canvasHeight: tgtH,
    x: (tgtW - drawW) / 2,
    y: (tgtH - drawH) / 2,
    width: drawW,
    height: drawH,
  };
};

interface CoverCropRect {
  srcX: number;
  srcY: number;
  srcW: number;
  srcH: number;
  destW: number;
  destH: number;
}

export const calculateCoverCrop = (srcW: number, srcH: number, destW: number, destH: number): CoverCropRect => {
  const srcRatio = srcW / srcH;
  const tgtRatio = destW / destH;
  
  let drawSrcW = srcW;
  let drawSrcH = srcH;
  
  if (srcRatio > tgtRatio) {
    drawSrcW = srcH * tgtRatio;
  } else {
    drawSrcH = srcW / tgtRatio;
  }
  
  return {
    srcX: (srcW - drawSrcW) / 2,
    srcY: (srcH - drawSrcH) / 2,
    srcW: drawSrcW,
    srcH: drawSrcH,
    destW,
    destH,
  };
};

export const renderImageToCanvas = (
  img: HTMLImageElement,
  config: ImageUploadConfig,
  metadata: ImageMetadata
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageEditorError('CANVAS_ERROR', 'Could not get canvas context');
  
  const destW = config.recommendedWidth;
  const destH = config.recommendedHeight;
  
  canvas.width = destW;
  canvas.height = destH;
  
  const bg = resolveCanvasBackground(config, metadata);
  if (bg !== 'transparent') {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  if (config.fitMode === 'contain') {
    const rect = calculateContainRect(img.naturalWidth, img.naturalHeight, destW, destH);
    ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height);
  } else {
    const rect = calculateCoverCrop(img.naturalWidth, img.naturalHeight, destW, destH);
    ctx.drawImage(img, rect.srcX, rect.srcY, rect.srcW, rect.srcH, 0, 0, rect.destW, rect.destH);
  }
  
  return canvas;
};

// Applies rotation, zoom and focal placement deterministically to canvas.
export const renderImageToCanvasWithState = (
  img: HTMLImageElement,
  config: ImageUploadConfig,
  metadata: ImageMetadata,
  state: EditorTransformState
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageEditorError('CANVAS_ERROR', 'Could not get canvas context');
  
  const destW = config.recommendedWidth;
  const destH = config.recommendedHeight;
  
  canvas.width = destW;
  canvas.height = destH;
  
  const bg = resolveStateBackground(state.backgroundMode, metadata);
  if (bg !== 'transparent') {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Find drawn dimensions based on natural source matching target geometry.
  let activeSrcW = img.naturalWidth;
  let activeSrcH = img.naturalHeight;
  
  // Account for rotation geometry swapping
  if (state.rotation === 90 || state.rotation === 270) {
    activeSrcW = img.naturalHeight;
    activeSrcH = img.naturalWidth;
  }

  const srcRatio = activeSrcW / activeSrcH;
  const tgtRatio = destW / destH;
  
  let targetFitW = destW;
  let targetFitH = destH;

  if (state.fitMode === 'contain') {
    if (srcRatio > tgtRatio) { targetFitH = destW / srcRatio; } 
    else { targetFitW = destH * srcRatio; }
  } else { 
    // cover
    if (srcRatio > tgtRatio) { targetFitW = destH * srcRatio; } 
    else { targetFitH = destW / srcRatio; }
  }

  // The size the image would draw at internally
  const zoomedW = targetFitW * state.zoom;
  const zoomedH = targetFitH * state.zoom;

  // Focal mapping 
  // focalX 0 = left edge aligned with canvas left
  // focalX 1 = right edge aligned with canvas right
  // focalX 0.5 = perfectly centered
  const excessW = zoomedW - destW;
  const excessH = zoomedH - destH;
  
  const x = destW / 2 - (excessW * (0.5 - state.focalX)) - zoomedW / 2;
  const y = destH / 2 - (excessH * (0.5 - state.focalY)) - zoomedH / 2;

  // Render process
  ctx.save();
  // Translate to center of where image will be
  ctx.translate(x + zoomedW / 2, y + zoomedH / 2);
  ctx.rotate((state.rotation * Math.PI) / 180);
  
  // After translating to center, draw it around center
  const renderW = (state.rotation === 90 || state.rotation === 270) ? zoomedH : zoomedW;
  const renderH = (state.rotation === 90 || state.rotation === 270) ? zoomedW : zoomedH;

  ctx.drawImage(img, -renderW / 2, -renderH / 2, renderW, renderH);
  ctx.restore();
  
  return canvas;
};

export const convertCanvasToFile = (
  canvas: HTMLCanvasElement, 
  mimeType: string, 
  quality: number, 
  fileName: string
): Promise<File> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new ImageEditorError('BLOB_ERROR', 'Failed to generate blob from canvas'));
          return;
        }
        
        const actualMime = blob.type;
        let ext = actualMime.split('/')[1] || 'jpg';
        if (ext === 'jpeg') ext = 'jpg';
        
        let newName = fileName;
        const lastDot = fileName.lastIndexOf('.');
        if (lastDot > -1) {
          newName = fileName.substring(0, lastDot) + '.' + ext;
        } else {
          newName += '.' + ext;
        }
        
        const f = new File([blob], newName, { type: actualMime });
        resolve(f);
      },
      mimeType,
      quality
    );
  });
};

export const compressImage = async (
  canvas: HTMLCanvasElement,
  config: ImageUploadConfig,
  fileName: string,
  metadata: ImageMetadata
): Promise<{ file: File, qualityStatus: string }> => {
  let outputMime = 'image/jpeg';
  if (config.preferredFormat === 'webp') outputMime = 'image/webp';
  if (config.preferredFormat === 'png') outputMime = 'image/png';
  
  if (metadata.hasTransparency && outputMime === 'image/jpeg') {
    outputMime = 'image/png';
  }

  const maxBytes = config.maxFilesizeKb * 1024;
  
  let currentQuality = config.compressionMode === 'lossless' ? 1.0 : (config.qualityTarget || 0.94);
  const minQuality = config.compressionMode === 'lossless' ? 1.0 : (config.minQuality || 0.70);
  let initialPass = true;
  let bestFile: File | null = null;
  
  while (currentQuality >= minQuality) {
    const file = await convertCanvasToFile(canvas, outputMime, currentQuality, fileName);
    
    if (!bestFile) bestFile = file;
    
    if (file.size <= maxBytes) {
      const status = initialPass ? (config.compressionMode === 'lossless' ? 'Lossless' : 'Visually lossless') : 'Optimization limit reached';
      return { file, qualityStatus: status };
    }
    
    initialPass = false;
    currentQuality -= 0.10;
  }

  // Fallback: If lossless PNG or initial pass failed to reach maxBytes, attempt lossy WebP
  if (bestFile && bestFile.size > maxBytes && SUPPORTED_WEB_IMAGES.includes('image/webp')) {
    let fallbackQuality = 0.90;
    while (fallbackQuality >= 0.70) {
      const fallbackFile = await convertCanvasToFile(canvas, 'image/webp', fallbackQuality, fileName);
      if (fallbackFile.size <= maxBytes) {
        return { file: fallbackFile, qualityStatus: 'Optimization via WebP fallback' };
      }
      fallbackQuality -= 0.10;
    }
  }
  
  if (bestFile && bestFile.size > maxBytes) {
     throw new ImageEditorError('TARGET_SIZE_UNREACHABLE_WITHOUT_QUALITY_LOSS', `Compression failed to hit ${config.maxFilesizeKb}KB while preserving visual targets.`);
  }

  const validBestFile = bestFile as File;
  return { file: validBestFile, qualityStatus: 'Optimization limit reached' };
};

export const transformImage = async (
  file: File,
  config: ImageUploadConfig
): Promise<TransformResult> => {
  const metadata = await readImageMetadata(file);

  if (metadata.width < config.minWidth || metadata.height < config.minHeight) {
    throw new ImageEditorError('DIMENSIONS_TOO_SMALL', 'Input dimensions are below required minimums.');
  }

  const img = await decodeImage(file);
  const canvas = renderImageToCanvas(img, config, metadata);
  
  const { file: finalFile } = await compressImage(canvas, config, file.name, metadata);
  const outputMetadata = await readImageMetadata(finalFile);
  const validationResult = await validateImageFile(finalFile, config);
  
  return {
    file: finalFile,
    metadata: outputMetadata,
    validationResult
  };
};

export const transformImageWithEditorState = async (
  file: File,
  config: ImageUploadConfig,
  state: EditorTransformState
): Promise<{ transform: TransformResult, qualityStatus: string }> => {
  const metadata = await readImageMetadata(file);

  if (metadata.width < config.minWidth || metadata.height < config.minHeight) {
    throw new ImageEditorError('DIMENSIONS_TOO_SMALL', 'Input dimensions are below required minimums.');
  }

  const img = await decodeImage(file);
  const canvas = renderImageToCanvasWithState(img, config, metadata, state);
  
  const { file: finalFile, qualityStatus } = await compressImage(canvas, config, file.name, metadata);
  const outputMetadata = await readImageMetadata(finalFile);
  const validationResult = await validateImageFile(finalFile, config);
  
  return {
    transform: {
      file: finalFile,
      metadata: outputMetadata,
      validationResult
    },
    qualityStatus
  };
};
